import {
  CalculateToPayResponse,
  CheckBankHourToUseResponse,
  PaidBankHour,
} from '../../../@types/bank-hour';
import { useFetch } from '../../../hooks/useFetch';
import { BankHour, BankHourHistory } from '../../../models/bank-hour';
import { errorNotification } from '../../../providers/mantine-notifications';
import { translateServerHttpErrors } from '../../../utils/helpers';
import {
  UseCalculateBankHoursProps,
  UseDeleteBankHoursProps,
  UseEntryBankHoursProps,
  UseGetBankHoursHistoryProps,
  UseGetBankHoursProps,
  UsePayBankHoursProps,
  UseSettleBankHoursProps,
  UseBulkSettleBankHoursDataProps,
  UseShowBankHoursProps,
  UseToUseBankHoursProps,
} from '../../../utils/types/data/hooks/bank-hour';

import { PaginatedResponse } from '../../../utils/types/data/pagination';
import {
  calculateBankHoursRequest,
  CheckBankHourToUse,
  entryHours,
  getBankHoursHistoryRequest,
  getBankHoursRequest,
  payBankHoursRequest,
  removeBankHourRequest,
  showBankHour,
  toUseBankHoursRequest,
} from '../../bank-hours';
import {
  settleBankHoursRequest,
  bulkSettleBankHoursDataRequest,
} from '../../exportables';

export function useGetBankHours() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PaginatedResponse<BankHour>>();

  const fetch = ({ onSuccess, params }: UseGetBankHoursProps) =>
    fetchData({
      fetcher: () => getBankHoursRequest(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useDeleteBankHour() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<void>();

  const fetch = ({ onSuccess, params }: UseDeleteBankHoursProps) =>
    fetchData({
      fetcher: () => removeBankHourRequest(params.id),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao remover banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
  };
}

export function usePayBankHour() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<PaidBankHour[]>();

  const fetch = ({ onSuccess, data }: UsePayBankHoursProps) =>
    fetchData({
      fetcher: () => payBankHoursRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao pagar banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
  };
}

export function useToUseBankHour() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<void>();

  const fetch = ({ onSuccess, data }: UseToUseBankHoursProps) =>
    fetchData({
      fetcher: () => toUseBankHoursRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao usar banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
  };
}

export function useCalculateBankHours() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<CalculateToPayResponse[]>();

  const fetch = ({ onSuccess, params }: UseCalculateBankHoursProps) =>
    fetchData({
      fetcher: () => calculateBankHoursRequest(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao calcular bancos de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
    response,
  };
}

export function useGetBankHoursHistory() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<PaginatedResponse<BankHourHistory>>();

  const fetch = ({ onSuccess, params }: UseGetBankHoursHistoryProps) =>
    fetchData({
      fetcher: () => getBankHoursHistoryRequest(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar histórico banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useShowBankHours() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<BankHour>();

  const fetch = ({ onSuccess, params }: UseShowBankHoursProps) =>
    fetchData({
      fetcher: () => showBankHour(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao buscar banco de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    response,
    error,
    loading,
  };
}

export function useCheckBankHoursToUse() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<CheckBankHourToUseResponse[]>();

  const fetch = ({ onSuccess, params }: UseCalculateBankHoursProps) =>
    fetchData({
      fetcher: () => CheckBankHourToUse(params),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao calcular bancos de horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
    response,
  };
}

export function useEntryBankHours() {
  const {
    fetchData,
    state: { data: response, error, loading },
  } = useFetch<void>();

  const fetch = ({ onSuccess, data, bankId }: UseEntryBankHoursProps) =>
    fetchData({
      fetcher: () => entryHours(bankId, data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao adicionar horas.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
    response,
  };
}

export function useSettleBankHours() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<any>();

  const fetch = ({ onSuccess, data }: UseSettleBankHoursProps) =>
    fetchData({
      fetcher: () => settleBankHoursRequest(data),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao gerar recibo de quitação.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
  };
}

export function useBulkSettleBankHoursData() {
  const {
    fetchData,
    state: { error, loading },
  } = useFetch<any>();

  const fetch = ({ onSuccess, records }: UseBulkSettleBankHoursDataProps) =>
    fetchData({
      fetcher: () => bulkSettleBankHoursDataRequest(records),
      onError: (error: any) => {
        errorNotification({
          title: 'Erro ao gerar quitação em massa.',
          message: translateServerHttpErrors(error, 'Tente novamente.'),
        });
      },
      onSuccess,
    });

  return {
    fetch,
    error,
    loading,
  };
}
